import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pause, Play, RotateCcw } from 'lucide-react';
import { DOMAINS_BY_SECTION, DOMAIN_LABELS, type SatDomain } from '@/api/sat';
import { Button } from '@/components/ui/button';
import { StopRing } from '@/components/sat/journey/StopRing';
import { SHORT_DOMAIN_LABELS } from '@/components/sat/journey/studentCopy';
import { StoryChip, STORY_CHIP_CSS } from './StoryChip';
import { COURSE_HREF } from '@/lib/marketingCtas';
import { cn } from '@/lib/utils';
import { recordOpenPick } from '@/lib/satOpenPick';
import { demoDomainMap } from '@/pages/open/openDemoDtos';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import {
  CHIPS,
  FLAG,
  STORY_FRAMES,
  STORY_UNDER_LINE,
  TINT_FILL_MS,
  TINT_STAGGER_MS,
  TRANSITION_MS,
  frame4SettledMs,
  hoverLine,
  pickCaption,
  ringStatesForFrame,
  storyStatus,
  withWeakest,
  type FrameNumber,
} from './storyFrames';

/**
 * The Story Stage (spec 010 §2): the course's own eight rings, played once, slowly.
 * Everything it shows comes from `storyFrames.ts`. Only transform / opacity /
 * stroke-dasharray move. Nothing animates until the stage is >= 50% in view, and
 * it never loops.
 */

const COURSE_DOMAINS: readonly SatDomain[] = [
  ...DOMAINS_BY_SECTION.READING_AND_WRITING,
  ...DOMAINS_BY_SECTION.MATH,
];

type Status = 'idle' | 'playing' | 'paused' | 'done';

interface Chip {
  key: number;
  ok: boolean;
  x: number;
  y: number;
  dx: number;
  dy: number;
}

export interface StoryStageProps {
  /** Anchor id. Homepage links to `#how-it-works`. */
  id?: string;
  /** `long` = the two-line captions used on /what-we-do/sat/about. */
  captions?: 'short' | 'long';
  /** Render the example line + course CTA under the stage. */
  showCta?: boolean;
  className?: string;
  /**
   * `hero` (HERO-V2): the stage as a framed product window — chrome bar, caption as the
   * dashboard headline, four-column rings, a status row. Default is the section stage.
   */
  variant?: 'section' | 'hero';
  /** Multiplies every hold, fill, stagger, chip flight and flag delay. Default 1. */
  speed?: number;
  /** Wait this long after the stage first reaches 50% in view before playing. Default 0. */
  autoplayDelayMs?: number;
  /**
   * Hydrate straight to this frame with no autoplay (reduced motion = 3, returning visitor = 4).
   * Reactive: it applies when it becomes defined, so a parent may decide after hydration.
   */
  initialFrame?: FrameNumber;
  /**
   * HOME-V3 (hero only): once hydrated, each ring is a button. Tapping one makes it the example
   * student's weakest, re-plans around it (frames 2 to 4) and writes `satOpenPick`.
   */
  pickable?: boolean;
  /** Told about every pick, and about `Replay` clearing it (null). */
  onPickChange?: (domain: SatDomain | null) => void;
}

function frameFor(n: FrameNumber) {
  return STORY_FRAMES[n - 1];
}

export function StoryStage({
  id = 'how-it-works',
  captions = 'short',
  showCta = true,
  className,
  variant = 'section',
  speed = 1,
  autoplayDelayMs = 0,
  initialFrame,
  pickable = false,
  onPickChange,
}: StoryStageProps) {
  const reduced = usePrefersReducedMotion();
  const hero = variant === 'hero';
  const sp = (ms: number) => Math.round(ms * speed);
  const [frame, setFrame] = useState<FrameNumber>(1);
  const [status, setStatus] = useState<Status>('idle');
  // Built on first play, never at render: the empty frame is prerendered and must be clock-free.
  const [scores, setScores] = useState<Record<string, number> | null>(null);
  const [sortReady, setSortReady] = useState(false);
  const [chips, setChips] = useState<Chip[]>([]);
  // A resting frame chosen by the parent (returning visitor): no fills, no re-sort slide, no fades.
  const [still, setStill] = useState(false);
  // HOME-V3: the visitor's guess, the status-row invitation, the hovered ring, and hydration.
  const [pick, setPick] = useState<SatDomain | null>(null);
  const [hovered, setHovered] = useState<SatDomain | null>(null);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const pickRef = useRef<SatDomain | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const gridRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<Map<SatDomain, HTMLLIElement>>(new Map());
  const prevPos = useRef<Map<SatDomain, { x: number; y: number }> | null>(null);
  const timers = useRef<number[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const frameStartedAt = useRef(0);
  const remainingMs = useRef(0);
  const scoresRef = useRef<Record<string, number> | null>(null);
  const autoplayed = useRef(false);
  const delayTimer = useRef<number | null>(null);
  const statusRef = useRef<Status>('idle');
  statusRef.current = status;

  const clearAll = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    if (advanceTimer.current != null) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
  };
  useEffect(
    () => () => {
      clearAll();
      if (delayTimer.current != null) window.clearTimeout(delayTimer.current);
    },
    [],
  );

  const setStatusBoth = (s: Status) => {
    statusRef.current = s;
    setStatus(s);
  };

  const demoScores = () => {
    const built: Record<string, number> = {};
    for (const d of demoDomainMap()) built[d.domain] = d.masterySnapshot.overall.scorePct ?? 0;
    return built;
  };

  const ensureScores = () => {
    if (scoresRef.current) return;
    const built = demoScores();
    scoresRef.current = built;
    setScores(built);
  };

  const setPickBoth = (d: SatDomain | null) => {
    pickRef.current = d;
    setPick(d);
    onPickChange?.(d);
  };

  const launchChips = () => {
    const cap = captionRef.current?.getBoundingClientRect();
    const grid = gridRef.current?.getBoundingClientRect();
    if (!cap || !grid) return;
    const sx = cap.left + Math.min(cap.width, 320) / 2 - grid.left;
    const sy = cap.bottom - grid.top;
    setChips(
      CHIPS.pattern.map((ok, i) => {
        const el = itemRefs.current.get(COURSE_DOMAINS[i]);
        const box = hero ? el?.querySelector<HTMLElement>('[data-ring-box]') : null;
        const tx = el ? el.offsetLeft + el.offsetWidth / 2 : sx;
        const ty = el ? (box ? el.offsetTop + box.offsetTop + box.offsetHeight / 2 : el.offsetTop + 30) : sy;
        return { key: i, ok, x: sx, y: sy, dx: tx - sx, dy: ty - sy };
      }),
    );
    timers.current.push(window.setTimeout(() => setChips([]), sp(CHIPS.totalMs) + 300));
  };

  const scheduleAdvance = (from: FrameNumber, ms: number) => {
    if (advanceTimer.current != null) window.clearTimeout(advanceTimer.current);
    frameStartedAt.current = performance.now() - (sp(frameFor(from).holdMs) - ms);
    remainingMs.current = ms;
    advanceTimer.current = window.setTimeout(() => enter((from + 1) as FrameNumber, true), ms);
  };

  /** Enter frame `n`. With `auto`, keep advancing on the frame clock. */
  function enter(n: FrameNumber, auto: boolean) {
    clearAll();
    setChips([]);
    setFrame(n);
    setSortReady(false);
    if (n === 2 && auto && !reduced) {
      // let the tint land before the grid re-sorts
      timers.current.push(window.setTimeout(() => setSortReady(true), sp(TINT_STAGGER_MS * 7 + TINT_FILL_MS + 300)));
    }
    if (n === 1 && auto && !reduced) window.requestAnimationFrame(launchChips);
    if (!auto) return;
    if (n === 4) {
      timers.current.push(window.setTimeout(() => setStatusBoth('done'), reduced ? 0 : sp(frame4SettledMs())));
      return;
    }
    scheduleAdvance(n, sp(frameFor(n).holdMs));
  }

  const play = () => {
    setStill(false);
    if (pickRef.current) {
      // `Replay` plays the original film: the guess is cleared and the demo spread restored.
      const original = demoScores();
      scoresRef.current = original;
      setScores(original);
      setPickBoth(null);
    }
    ensureScores();
    setStatusBoth('playing');
    enter(1, true);
  };

  const pause = () => {
    if (advanceTimer.current != null) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = null;
    remainingMs.current = Math.max(0, sp(frameFor(frame).holdMs) - (performance.now() - frameStartedAt.current));
    setStatusBoth('paused');
  };

  const resume = () => {
    if (frame === 4) {
      setStatusBoth('done');
      return;
    }
    setStatusBoth('playing');
    scheduleAdvance(frame, remainingMs.current || sp(frameFor(frame).holdMs));
  };

  const jump = (n: FrameNumber, rest = false) => {
    setStill(rest);
    ensureScores();
    if (statusRef.current === 'playing') {
      enter(n, true);
      return;
    }
    clearAll();
    setChips([]);
    setFrame(n);
    setSortReady(true);
    remainingMs.current = sp(frameFor(n).holdMs);
    setStatusBoth(n === 4 ? 'done' : 'paused');
  };

  /** Tap a ring: it becomes the weakest, and frames 2 to 4 replay around it. */
  const pickDomain = (d: SatDomain) => {
    if (!pickable) return;
    autoplayed.current = true; // the pick wins over a pending autoplay
    if (delayTimer.current != null) window.clearTimeout(delayTimer.current);
    clearAll();
    setHovered(null);
    const next = withWeakest(demoScores(), d);
    scoresRef.current = next;
    setScores(next);
    setPickBoth(d);
    recordOpenPick(d, new Date().toISOString());
    if (reduced) {
      jump(3, true);
      return;
    }
    setStill(false);
    setStatusBoth('playing');
    enter(2, true);
  };

  const hoverRing = (d: SatDomain | null, force = false) => {
    if (d == null) return setHovered(null);
    if (!atRest) return;
    const fine = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;
    if (fine || force) setHovered(d);
  };

  const toggle = () => {
    if (status === 'playing') pause();
    else if (status === 'paused') resume();
    else play();
  };

  // A parent-chosen resting frame wins over autoplay (and may arrive after hydration).
  const stillRef = useRef(false);
  stillRef.current = still;
  const initialRef = useRef<FrameNumber | undefined>(initialFrame);
  initialRef.current = initialFrame;
  useEffect(() => {
    if (initialFrame == null) return;
    autoplayed.current = true;
    if (delayTimer.current != null) window.clearTimeout(delayTimer.current);
    jump(initialFrame, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFrame]);

  // Plays once, when the stage is >= 50% in view. Never under reduced motion.
  useEffect(() => {
    if (reduced || typeof IntersectionObserver === 'undefined') return;
    const el = panelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        // the observer reports once on observe() for ANY overlap; only >= 50% counts
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || autoplayed.current) return;
        autoplayed.current = true;
        io.disconnect();
        if (initialRef.current != null) return;
        if (autoplayDelayMs > 0) {
          delayTimer.current = window.setTimeout(() => {
            if (statusRef.current === 'idle' && initialRef.current == null) play();
          }, autoplayDelayMs);
        } else if (statusRef.current === 'idle') play();
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced]);

  // At rest = the film is over, or a still frame was chosen (returning visitor, reduced motion).
  const atRest = status === 'done' || (reduced && status !== 'idle');
  useEffect(() => setHovered(null), [atRest, pick]);

  const states = useMemo(() => ringStatesForFrame(frame, COURSE_DOMAINS, scores), [frame, scores]);
  const sorted = frame >= 3 || (frame === 2 && (sortReady || reduced || status !== 'playing'));
  const orderOf = (i: number) => (sorted ? states[i].order : i);
  const orderKey = states.map((_, i) => orderOf(i)).join(',');

  // FLIP: slide each ring from where it was to where it is now (transform only).
  useLayoutEffect(() => {
    const next = new Map<SatDomain, { x: number; y: number }>();
    for (const d of COURSE_DOMAINS) {
      const el = itemRefs.current.get(d);
      if (el) next.set(d, { x: el.offsetLeft, y: el.offsetTop });
    }
    const prev = prevPos.current;
    if (prev && !reduced && !stillRef.current) {
      for (const d of COURSE_DOMAINS) {
        const el = itemRefs.current.get(d);
        const from = prev.get(d);
        const to = next.get(d);
        if (!el || !from || !to || typeof el.animate !== 'function') continue;
        const dx = from.x - to.x;
        const dy = from.y - to.y;
        if (!dx && !dy) continue;
        el.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
          { duration: TRANSITION_MS, easing: 'cubic-bezier(0.3, 0.7, 0.2, 1)' },
        );
      }
    }
    prevPos.current = next;
  }, [orderKey, reduced]);

  const rank = useMemo(() => {
    const byScore = scores ? [...COURSE_DOMAINS].sort((a, b) => (scores[a] ?? 0) - (scores[b] ?? 0)) : null;
    return {
      weakest: byScore ? SHORT_DOMAIN_LABELS[byScore[0]] : '',
      second: byScore ? SHORT_DOMAIN_LABELS[byScore[1]] : '',
    };
  }, [scores]);
  const filmLine = storyStatus(frame, rank, frame === 4 && (status === 'done' || still || reduced));
  const hoverText = hovered && atRest ? hoverLine(hovered) : null;
  const statusKind = hoverText ? 'hover' : 'film';
  const statusLine = hoverText ?? filmLine;
  const caption =
    pick && frame === 2
      ? pickCaption(SHORT_DOMAIN_LABELS[pick])
      : captions === 'long'
        ? frameFor(frame).longCaption
        : frameFor(frame).caption;
  const animate = !reduced && !still;
  const buttonLabel = status === 'playing' ? 'Pause' : status === 'done' ? 'Replay' : 'Play';
  const ButtonIcon = status === 'playing' ? Pause : status === 'done' ? RotateCcw : Play;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (reduced || e.target !== e.currentTarget) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <section id={id} aria-labelledby={`${id}-title`} className={cn('scroll-mt-20', className)} data-testid="story-stage">
      <style>{STAGE_CSS}</style>
      <h2 id={`${id}-title`} className="sr-only">
        How the free SAT course works
      </h2>

      <div
        ref={panelRef}
        role="group"
        tabIndex={reduced ? undefined : 0}
        aria-label="The course, start to finish. Press space to pause or play."
        onKeyDown={onKeyDown}
        data-frame={frame}
        data-status={status}
        data-pick={pick ?? undefined}
        data-sorted={sorted}
        className={cn(
          'border bg-card outline-none focus-visible:ring-2 focus-visible:ring-primary',
          hero
            ? 'overflow-hidden rounded-2xl shadow-[0_1px_0_hsl(var(--border)),0_32px_64px_-32px_hsl(var(--primary)/0.5)]'
            : 'rounded-3xl px-4 pb-4 pt-7 shadow-sm sm:px-8 sm:pt-9',
        )}
      >
        {hero && (
          <div className="flex items-center justify-between gap-3 border-b bg-muted/50 px-4 py-2.5" data-testid="story-chrome">
            <span className="flex items-center gap-2 text-xs font-semibold text-foreground">
              <span aria-hidden className="h-2 w-2 rounded-full bg-primary" />
              Example student
            </span>
            <span className="text-xs font-medium text-muted-foreground">Free SAT course</span>
          </div>
        )}
        <div className={hero ? 'px-4 pb-1 pt-4 sm:px-6 sm:pt-5' : undefined}>
        <p
          ref={captionRef}
          aria-live="polite"
          data-testid="story-caption"
          className={cn(
            hero
              ? 'min-h-[3rem] text-balance text-lg font-semibold leading-snug tracking-tight sm:min-h-[1.75rem] sm:text-xl'
              : 'max-w-[28ch] text-balance text-2xl font-semibold leading-tight tracking-tight sm:max-w-[36ch] md:text-4xl',
            !hero && (captions === 'long' ? 'min-h-[7.5rem] md:min-h-[6.5rem]' : 'min-h-[3.75rem] md:min-h-[3.25rem]'),
          )}
        >
          <span key={frame} className={animate ? 'ss-caption-in inline-block' : 'inline-block'}>
            {caption}
          </span>
        </p>

        <div className={cn('relative', hero ? 'mt-4' : 'mt-8 md:mt-12')}>
          <ul
            ref={gridRef}
            className={cn(
              'relative grid grid-cols-4 gap-x-1',
              hero ? 'gap-y-4 sm:gap-y-5' : 'gap-y-9 md:grid-cols-8 md:gap-y-10',
            )}
            aria-label="Eight SAT areas"
          >
            {states.map((r, i) => {
              const label = SHORT_DOMAIN_LABELS[r.domain];
              const fill = reduced || still ? 'instant' : r.fill;
              const centerDelay =
                fill === 'instant' ? 0 : Math.round(fill.delayMs + fill.ms * 0.7);
              const scaledFill = fill === 'instant' ? fill : { ms: sp(fill.ms), delayMs: sp(fill.delayMs) };
              return (
                <li
                  key={r.domain}
                  ref={(el) => {
                    if (el) itemRefs.current.set(r.domain, el);
                    else itemRefs.current.delete(r.domain);
                  }}
                  style={{ order: orderOf(i) }}
                  data-testid="story-ring"
                  data-domain={r.domain}
                  data-mastered={r.mastered}
                  data-you-are-here={r.youAreHere}
                  className="relative flex flex-col items-center gap-2 text-center"
                >
                  <div
                    data-ring-box={hero ? '' : undefined}
                    className={cn(
                      'relative',
                      hero
                        ? 'h-12 w-12 sm:h-16 sm:w-16 lg:h-[4.5rem] lg:w-[4.5rem]'
                        : 'origin-center md:scale-[1.3]',
                      hero && pickable && 'transition-transform duration-150',
                      hoverText && hovered === r.domain && 'scale-[1.04]',
                    )}
                  >
                    {r.youAreHere && (
                      <span
                        key="here"
                        data-testid="story-you-are-here"
                        className={cn(
                          'pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-semibold text-primary-foreground shadow',
                          animate && 'ss-fade-in',
                        )}
                      >
                        You are here
                      </span>
                    )}
                    <div className={hero ? 'origin-top-left scale-[.75] sm:scale-100 lg:scale-[1.125]' : undefined}>
                    <StopRing scorePct={r.pct} band={r.band} unearned fill={scaledFill} tone={hero ? r.domain : undefined}>
                      <span className={cn('z-10 font-bold tracking-tight text-foreground', hero ? 'text-[15px] sm:text-[13px]' : 'text-[13px]')}>
                        {r.pct != null ? (
                          <span
                            key={r.pct}
                            className={cn('block', animate && 'ss-fade-in')}
                            style={animate ? { animationDelay: `${sp(centerDelay)}ms` } : undefined}
                          >
                            {r.pct}%
                          </span>
                        ) : (
                          <span className="block h-2 w-2 rounded-full bg-muted-foreground/20" aria-hidden />
                        )}
                      </span>
                    </StopRing>
                    </div>
                    {r.flagDelayMs != null && (
                      <span
                        key="flag"
                        data-testid="story-flag"
                        className={cn(
                          'absolute -right-1.5 -top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow ring-2 ring-card',
                          animate && 'ss-flag',
                        )}
                        style={animate ? { animationDelay: `${sp(r.flagDelayMs)}ms` } : undefined}
                        role="img"
                        aria-label={`${label} proven`}
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={3.5} aria-hidden />
                      </span>
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-balance font-medium leading-tight',
                      hero ? 'text-[11px] sm:text-xs' : 'mt-1 text-xs md:mt-2',
                    )}
                  >
                    {label}
                  </span>
                  {pickable && hydrated && (
                    // An overlay, not a wrapper: the ring and label keep their DOM (no remount, no
                    // layout change) and the prerendered HTML holds no button at all.
                    <button
                      type="button"
                      data-testid="story-ring-button"
                      aria-label={`${DOMAIN_LABELS[r.domain]}. Make this the example student's weakest area.`}
                      aria-pressed={pick === r.domain}
                      onClick={() => pickDomain(r.domain)}
                      onPointerEnter={() => hoverRing(r.domain)}
                      onPointerLeave={() => hoverRing(null)}
                      onFocus={(e) => e.currentTarget.matches(':focus-visible') && hoverRing(r.domain, true)}
                      onBlur={() => hoverRing(null)}
                      className="absolute -top-3 bottom-0 -left-1 -right-1 z-20 cursor-pointer rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    />
                  )}
                </li>
              );
            })}
          </ul>

          {chips.map((chip) => (
            <StoryChip
              key={chip.key}
              ok={chip.ok}
              x={chip.x}
              y={chip.y}
              dx={chip.dx}
              dy={chip.dy}
              delayMs={sp(CHIPS.staggerMs * chip.key)}
              flightMs={sp(CHIPS.flightMs)}
            />
          ))}
        </div>

        {hero && (
          <p
            data-testid="story-status"
            className="mt-3 flex min-h-6 items-center gap-2 border-t pt-3 text-[13px] font-medium text-foreground"
          >
            <span aria-hidden className="text-primary">▸</span>
            <span key={`${frame}-${statusKind}-${hovered ?? ''}`} className={cn('min-w-0 truncate', animate && statusKind !== 'hover' && 'ss-caption-in')}>
              {statusLine}
            </span>
          </p>
        )}

        <div className={cn('flex items-center justify-between gap-3', hero ? 'mt-1' : 'mt-6 border-t pt-2')}>
          <div
            role={reduced ? 'tablist' : 'group'}
            aria-label={reduced ? 'Course frames' : 'Jump to a frame'}
            className="flex flex-1 items-center gap-2 sm:max-w-sm"
          >
            {STORY_FRAMES.map((f) => {
              const active = f.n === frame;
              const past = f.n < frame;
              const filling = active && (status === 'playing' || status === 'paused') && f.n !== 4 && animate;
              return (
                <button
                  key={f.n}
                  type="button"
                  role={reduced ? 'tab' : undefined}
                  aria-selected={reduced ? active : undefined}
                  aria-current={!reduced && active ? 'step' : undefined}
                  aria-label={`Frame ${f.n} of 4: ${f.caption}`}
                  data-testid={`story-dot-${f.n}`}
                  onClick={() => jump(f.n)}
                  className="group flex h-11 flex-1 items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="relative block h-1.5 w-full overflow-hidden rounded-full bg-border">
                    <span
                      key={`${f.n}-${active ? frame : ''}-${filling ? 'f' : 's'}`}
                      className={cn(
                        'absolute inset-0 origin-left rounded-full bg-primary',
                        filling && 'ss-bar',
                        !filling && !past && !(active && (status !== 'idle' || f.n === 4)) && 'scale-x-0',
                        active && status === 'idle' && 'scale-x-100 bg-primary/25',
                      )}
                      style={filling ? { animationDuration: `${sp(f.holdMs)}ms` } : undefined}
                    />
                  </span>
                </button>
              );
            })}
          </div>
          {!reduced && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 shrink-0 gap-1.5 px-3"
              onClick={toggle}
              data-testid="story-toggle"
            >
              <ButtonIcon className={cn('!size-3.5', status !== 'done' && 'fill-current')} aria-hidden />
              {buttonLabel}
            </Button>
          )}
        </div>
        </div>
      </div>

      {showCta && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-prose text-sm text-muted-foreground">{STORY_UNDER_LINE}</p>
          <Button asChild className="w-full sm:w-auto">
            <Link to={COURSE_HREF}>Start the free course →</Link>
          </Button>
        </div>
      )}
    </section>
  );
}

const STAGE_CSS = `${STORY_CHIP_CSS}
@keyframes ss-flag { from { opacity: 0; transform: scale(${FLAG.fromScale}); } to { opacity: 1; transform: scale(1); } }
@keyframes ss-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes ss-caption-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes ss-bar { from { transform: scaleX(0); } to { transform: scaleX(1); } }
.ss-flag { animation: ss-flag ${FLAG.popMs}ms ${FLAG.easing} both; }
.ss-fade-in { animation: ss-fade-in 300ms ease-out both; }
.ss-caption-in { animation: ss-caption-in ${TRANSITION_MS / 2}ms ease-out both; }
.ss-bar { animation: ss-bar linear both; }
[data-status="paused"] .ss-bar { animation-play-state: paused; }
@media (prefers-reduced-motion: reduce) {
  .ss-flag, .ss-fade-in, .ss-caption-in, .ss-bar { animation: none !important; }
}
`;
